import { ChangeEvent, forwardRef, ReactNode, useMemo } from "react";

const IconWrapper: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div">
> = (props) => (
  <div
    {...props}
    className="flex h-full grow-0 items-center justify-center text-zi-placeholder"
  />
);

type InputProps = Omit<
  React.ComponentPropsWithoutRef<"input">,
  "onChange" | "size" | "type" | "value"
>;

export type Props = InputProps & {
  value?: string;
  onChange?: (value: string) => void;
  type?: "email" | "password" | "search" | "tel" | "text";
  size?: "small" | "medium";
  isErrored?: boolean;
  renderIconBefore?: () => ReactNode;
  renderIconAfter?: () => ReactNode;
};

export const TextInput = forwardRef<HTMLInputElement, Props>(
  ({ onChange, isErrored, size, ...props }, ref) => {
    const handleChange = useMemo(() => {
      return onChange !== undefined
        ? (e: ChangeEvent<HTMLInputElement>) => {
            onChange(e.target.value);
          }
        : undefined;
    }, [onChange]);

    return (
      <div
        data-errored={isErrored}
        data-size={size}
        className="flex h-[50px] flex-row flex-nowrap items-center gap-x-2.5 overflow-hidden rounded-lg border-2 border-zi-lightGray bg-zi-superLightGray px-4 py-0 text-[17px] text-zi-text transition-colors duration-150 focus-within:border-zi-primary data-[size=small]:h-10 data-[errored=true]:border-zi-formError data-[size=small]:text-[15px]"
      >
        {props.renderIconBefore && (
          <IconWrapper>{props.renderIconBefore()}</IconWrapper>
        )}
        <input
          ref={ref}
          onChange={handleChange}
          {...props}
          disabled={props.disabled ?? false}
          value={props.value ?? ""}
          className="block h-full w-0 grow border-none bg-transparent px-0 py-4 text-inherit outline-none placeholder:text-zi-placeholder"
        />
        {props.renderIconAfter && (
          <IconWrapper>{props.renderIconAfter()}</IconWrapper>
        )}
      </div>
    );
  }
);

TextInput.displayName = "TextInput";
